import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import { Icon } from '@breezeway/brzw-components';
import { getReservationGuestText } from '../../../utils/reservations';
import ReservationBar from './reservation_bar';
import { SCHEDULE_MODE } from '../../constants';

import './reservation.scss';

class Reservation extends Component {
  constructor(props) {
    super(props);

    this.CLASS_NAME = 'brzw-calendar-reservation';

    this.ellipsisDifferentReservationDayAfterDay = this.ellipsisDifferentReservationDayAfterDay.bind(
      this,
    );
    this.getIdsByDate = this.getIdsByDate.bind(this);
    this.isReservationWithAlert = this.isReservationWithAlert.bind(this);
    this.renderReservationBar = this.renderReservationBar.bind(this);
    this.renderReservationText = this.renderReservationText.bind(this);
  }

  shouldComponentUpdate(nextProps) {
    return (
      !_.isEqual(this.props, nextProps) ||
      !_.isEqual(this.props.firstReservations, nextProps.firstReservations)
    );
  }

  getIdsByDate() {
    return _.reduce(
      this.props.firstReservations,
      (result, value, key) => {
        result[value] = [...(result[value] || []), key];
        return result;
      },
      {},
    );
  }

  ellipsisDifferentReservationDayAfterDay() {
    const { checkin, reservationPropsNextDay } = this.props;

    const isCheckinNotEmpty = !_.isEmpty(_.get(checkin, 'checkin_date'));

    return (
      isCheckinNotEmpty &&
      _.get(reservationPropsNextDay, 'occupied.id') === _.get(checkin, 'id') &&
      !_.isEmpty(_.get(reservationPropsNextDay, 'checkout.checkout_date')) &&
      moment(reservationPropsNextDay.checkout.checkout_date).diff(
        moment(checkin.checkin_date),
        'days',
      ) === 1
    );
  }

  isReservationWithAlert(reservation) {
    const { alerts } = this.props;

    return _.some(alerts, alert => _.isEqual(_.get(alert, 'id'), _.get(reservation, 'id')));
  }

  renderReservationBar(className, reservation, baseClassName, type, alert) {
    const {
      cellDate,
      checkin,
      checkout,
      firstReservations,
      flyoutInformations,
      hoveredReservation,
      numberOfOccupied,
      occupied,
      setFirstIdOfReservation,
      setHoveredReservation,
    } = this.props;

    return (
      <ReservationBar
        alert={alert}
        baseClassName={baseClassName}
        cellDate={cellDate}
        checkin={checkin}
        checkout={checkout}
        className={className}
        firstReservations={firstReservations}
        flyoutInformations={flyoutInformations}
        hoveredReservation={hoveredReservation}
        numberOfOccupied={numberOfOccupied}
        occupied={occupied}
        reservation={reservation}
        setFirstIdOfReservation={setFirstIdOfReservation}
        setHoveredReservation={setHoveredReservation}
        type={type}
      />
    );
  }

  renderReservationText(type) {
    const {
      alerts,
      cellWidth,
      checkin,
      checkinHightlighted,
      checkout,
      empty,
      grayedOut,
      mode,
      numberOfOccupied,
      occupied,
      occupiedHightlighted,
      reservationTextDuration,
    } = this.props;

    if (!occupied && !checkin && !checkout && !empty) {
      return null;
    }

    const imposedCheckout = checkout && occupied;
    const imposedCheckoutOccupied = imposedCheckout && numberOfOccupied === 1;
    const multiOccupiedCheckout = checkin && numberOfOccupied <= 1;
    const isDaily = mode === SCHEDULE_MODE.DAILY;
    const smallEmptySpace = 18;
    const bigEmptySpace = 26; // is sum with small = 44
    let reservation = type;

    if (_.isEmpty(type)) {
      reservation = _.isEmpty(checkin) ? occupied : checkin;
    }

    const id = _.get(reservation, 'id');
    const reservationDuration = _.get(reservationTextDuration, [id, 'duration']);
    const reservationConflictsDates = _.get(reservationTextDuration, [id, 'conflicts_dates']);

    const countReservationWidth = cellWidth * reservationDuration - smallEmptySpace;
    const reservationWithAlertAndLongText =
      !_.isEmpty(alerts) && _.isEmpty(reservationConflictsDates);
    const countWidth = reservationWithAlertAndLongText
      ? countReservationWidth - bigEmptySpace
      : countReservationWidth;
    const maxWidth = _.isNumber(countWidth) ? countWidth : 'auto';
    const reservationText = getReservationGuestText(reservation, true);

    return (
      <span
        className={classnames(`${this.CLASS_NAME}__text`, {
          [`${this.CLASS_NAME}__text--with-imposed-checkout-occupied`]: imposedCheckoutOccupied,
          [`${this.CLASS_NAME}__text--occupied`]: occupied && !multiOccupiedCheckout,
          [`${this.CLASS_NAME}__text--daily`]: isDaily,
          [`${this.CLASS_NAME}__text--ellipsis`]: this.ellipsisDifferentReservationDayAfterDay(),
          [`${this.CLASS_NAME}__text--highlight`]: occupiedHightlighted || checkinHightlighted,
          [`${this.CLASS_NAME}__text--gray-out`]:
            grayedOut && !(occupiedHightlighted || checkinHightlighted),
        })}
        style={{ maxWidth }}
        title={reservationText}
      >
        {reservationText}
      </span>
    );
  }

  render() {
    const {
      alerts,
      cellDate,
      checkin,
      checkinHightlighted,
      checkout,
      checkoutHightlighted,
      containerClassName: propsContainerClassName,
      empty,
      firstDataInRow,
      firstDayOnCalendar,
      flyoutInformations,
      grayedOut,
      hoveredReservation,
      mode,
      nextReservationDate,
      nextReservationReadyStatus,
      numberOfOccupied,
      occupied,
      occupiedHightlighted,
      reservationByDate,
      reservationPropsNextDay,
    } = this.props;

    if (!occupied && !checkin && !checkout && !empty) {
      return null;
    }

    const formattedCellData = _.invoke(cellDate, 'format', 'YYYY-MM-DD');

    let readiness = null;
    let readinessIsNotNull = false;
    const isDaily = mode === SCHEDULE_MODE.DAILY;

    const grayBgr = checkin || checkout || occupied;
    const checkInOnly = checkin && !checkout && !occupied;
    const checkOutOnly = !checkin && checkout && !occupied;
    const occupiedOnly = !checkin && !checkout && occupied;
    const checkInWithCheckOut = checkin && checkout && !occupied;

    const imposedCheckout = checkout && occupied;
    const checkinDate = _.get(checkin, 'checkin_date', null);
    const flyoutReservationId = _.get(flyoutInformations, 'reservationId', 0);

    const blueBgr =
      (checkin && checkinHightlighted) ||
      (checkout && checkoutHightlighted) ||
      (occupied && occupiedHightlighted);

    const darkGrayBgr =
      (checkin && grayedOut && !checkinHightlighted) ||
      (checkout && grayedOut && !checkoutHightlighted) ||
      (occupied && grayedOut && !occupiedHightlighted);
    const imposedCheckoutOnFirstDay = imposedCheckout && firstDayOnCalendar;
    const shouldDisplayReservationText =
      (occupied && isDaily) ||
      checkin ||
      (firstDataInRow && !checkout) ||
      imposedCheckoutOnFirstDay;

    const countButtons = _.get(_.countBy([checkout, checkin, occupied], _.isObject), 'true');
    const containerHaveTwoButtons = countButtons >= 2;
    const containerHaveOneButton = countButtons === 1;
    const containerHaveNoneButtons = countButtons === 0;

    const alertOccupied = this.isReservationWithAlert(occupied);
    const alertCheckin = this.isReservationWithAlert(checkin);
    const alertCheckout = this.isReservationWithAlert(checkout);

    const withAlert = alertOccupied || alertCheckin || alertCheckout;
    const alertOnCheckout = !_.isEmpty(alerts) && alertCheckout;
    const nextDayIsEmpty =
      _.get(reservationPropsNextDay, 'checkin') === null &&
      _.get(reservationPropsNextDay, 'checkout') === null &&
      _.get(reservationPropsNextDay, 'occupied') === null;

    const displayWarningIcon = alertOnCheckout || (withAlert && nextDayIsEmpty);

    const containerClassName = classnames(
      this.CLASS_NAME,
      propsContainerClassName,
      `${this.CLASS_NAME}--${mode}-mode`,
      {
        [`${this.CLASS_NAME}--blue-bgr`]: blueBgr,
        [`${this.CLASS_NAME}--curled`]:
          reservationByDate &&
          !_.isEmpty(this.getIdsByDate()) &&
          _.isEmpty(_.get(this.getIdsByDate(), formattedCellData)),
        [`${this.CLASS_NAME}--dark-gray-bgr`]: darkGrayBgr,
        [`${this.CLASS_NAME}--empty`]: empty,
        [`${this.CLASS_NAME}--gray-bgr`]: grayBgr,
        [`${this.CLASS_NAME}--first-day-of-month`]: firstDayOnCalendar,
        [`${this.CLASS_NAME}--imposed-checkin`]: checkin && occupied,
        [`${this.CLASS_NAME}--imposed-checkout`]: imposedCheckout,
        [`${this.CLASS_NAME}--checkin-only`]: checkInOnly,
        [`${this.CLASS_NAME}--checkin-with-checkout`]: checkInWithCheckOut,
        [`${this.CLASS_NAME}--checkout-only`]: checkOutOnly,
        [`${this.CLASS_NAME}--occupied-only`]: occupiedOnly,
        [`${this.CLASS_NAME}--multiple-occupied`]: numberOfOccupied > 1,
        [`${this.CLASS_NAME}--with-alert`]: withAlert,
        [`${this.CLASS_NAME}--with-alert-occupied`]: alertOccupied,
        [`${this.CLASS_NAME}--with-alert-checkin`]: alertCheckin,
        [`${this.CLASS_NAME}--with-alert-checkout`]: alertCheckout,
        [`${this.CLASS_NAME}__container--two-children`]: containerHaveTwoButtons,
        [`${this.CLASS_NAME}__container--one-child`]: containerHaveOneButton,
        [`${this.CLASS_NAME}__container--none-children`]: containerHaveNoneButtons,
        [`${this.CLASS_NAME}--with-highlighted-normal-occupied`]:
          flyoutReservationId === _.get(occupied, 'id'),
        [`${this.CLASS_NAME}--with-highlighted-normal-checkout`]:
          flyoutReservationId === _.get(checkout, 'id'),
        [`${this.CLASS_NAME}--with-highlighted-normal-checkin`]:
          flyoutReservationId === _.get(checkin, 'id'),
        [`${this.CLASS_NAME}--with-hovered-normal-occupied`]:
          hoveredReservation === _.get(occupied, 'id'),
        [`${this.CLASS_NAME}--with-hovered-normal-checkout`]:
          hoveredReservation === _.get(checkout, 'id'),
        [`${this.CLASS_NAME}--with-hovered-normal-checkin`]:
          hoveredReservation === _.get(checkin, 'id'),
      },
    );

    if (checkinDate && nextReservationDate) {
      const checkinMoment = moment(checkinDate);
      const nextDateMoment = moment(nextReservationDate);

      if (checkinMoment.isSame(nextDateMoment)) {
        readiness = nextReservationReadyStatus;
        readinessIsNotNull = readiness !== null;
      }
    }

    const checkinWrapperClassName = classnames(`${this.CLASS_NAME}__checkin-wrapper`, {
      [`${this.CLASS_NAME}__checkin-wrapper--highlight`]: checkinHightlighted,
      [`${this.CLASS_NAME}__checkin-wrapper--gray-out`]: grayedOut && !checkinHightlighted,
    });

    const checkoutWrapperClassName = classnames(`${this.CLASS_NAME}__checkout-wrapper`, {
      [`${this.CLASS_NAME}__checkout-wrapper--highlight`]: checkoutHightlighted,
      [`${this.CLASS_NAME}__checkout-wrapper--gray-out`]: grayedOut && !checkoutHightlighted,
    });

    const occupiedClassName = classnames(`${this.CLASS_NAME}__occupied-wrapper`, {
      [`${this.CLASS_NAME}__occupied-wrapper--highlight`]: occupiedHightlighted,
      [`${this.CLASS_NAME}__occupied-wrapper--gray-out`]: grayedOut && !occupiedHightlighted,
    });

    const multiOccupiedCheckout = checkin && numberOfOccupied <= 1;
    const buttonsContainerClassName = classnames(`${this.CLASS_NAME}__container`, {
      [`${this.CLASS_NAME}__container--checkin-not-ready`]: readinessIsNotNull && !readiness,
      [`${this.CLASS_NAME}__container--checkin-ready`]: readinessIsNotNull && readiness,
      [`${this.CLASS_NAME}__container--with-separator`]: checkin || checkout,
    });

    return (
      <div className={containerClassName}>
        {imposedCheckoutOnFirstDay && this.renderReservationText(checkout)}
        {shouldDisplayReservationText && this.renderReservationText()}

        <div className={buttonsContainerClassName}>
          {empty && <div className={`${this.CLASS_NAME}__empty-wrapper`} />}
          {multiOccupiedCheckout &&
            occupied &&
            this.renderReservationBar(
              occupiedClassName,
              occupied,
              `${this.CLASS_NAME}__occupied-wrapper`,
              'occupied',
              _.get(occupied, 'alert'),
            )}

          {checkout &&
            this.renderReservationBar(
              checkoutWrapperClassName,
              checkout,
              `${this.CLASS_NAME}__checkout-wrapper`,
              'checkout',
              _.get(checkout, 'alert'),
            )}

          {checkin &&
            this.renderReservationBar(
              checkinWrapperClassName,
              checkin,
              `${this.CLASS_NAME}__checkin-wrapper`,
              'checkin',
              _.get(checkin, 'alert'),
            )}

          {!multiOccupiedCheckout &&
            occupied &&
            this.renderReservationBar(
              occupiedClassName,
              occupied,
              `${this.CLASS_NAME}__occupied-wrapper`,
              'occupied',
              _.get(occupied, 'alert'),
            )}

          {displayWarningIcon && (
            <Icon
              iconName="warning-outline"
              className={classnames(`${this.CLASS_NAME}__warning-icon`, {
                '-on-checkout': alertOnCheckout,
              })}
            />
          )}
        </div>
      </div>
    );
  }
}

Reservation.propTypes = {
  alerts: PropTypes.arrayOf(PropTypes.shape({})),
  cellDate: PropTypes.shape({}),
  cellWidth: PropTypes.number,
  checkin: PropTypes.shape({}),
  checkinHightlighted: PropTypes.bool,
  checkout: PropTypes.shape({}),
  checkoutHightlighted: PropTypes.bool,
  containerClassName: PropTypes.string,
  empty: PropTypes.bool,
  firstDataInRow: PropTypes.bool,
  firstDayOnCalendar: PropTypes.bool,
  firstReservations: PropTypes.shape({}),
  flyoutInformations: PropTypes.shape({
    reservationId: PropTypes.number,
  }),
  grayedOut: PropTypes.bool,
  hoveredReservation: PropTypes.number,
  mode: PropTypes.string,
  nextReservationDate: PropTypes.string,
  nextReservationReadyStatus: PropTypes.any,
  numberOfOccupied: PropTypes.number,
  occupied: PropTypes.shape({}),
  occupiedHightlighted: PropTypes.bool,
  reservationByDate: PropTypes.bool,
  reservationPropsNextDay: PropTypes.shape({}),
  reservationTextDuration: PropTypes.shape({}),
  setFirstIdOfReservation: PropTypes.func,
  setHoveredReservation: PropTypes.func,
};

Reservation.defaultProps = {
  cellWidth: 0,
  checkin: null,
  checkinHightlighted: false,
  checkout: null,
  checkoutHightlighted: false,
  containerClassName: '',
  empty: false,
  firstDataInRow: false,
  firstReservations: {},
  grayedOut: false,
  mode: SCHEDULE_MODE.DAILY,
  nextReservationDate: '',
  nextReservationReadyStatus: null,
  numberOfOccupied: 0,
  occupied: null,
  occupiedHightlighted: null,
  reservationTextDuration: {},
};

export default Reservation;
